import { auth, db, functions } from './firebase';
import { 
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, serverTimestamp, setDoc, writeBatch
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Organization } from '../types';

// Helper to mimic Axios response
const toAxios = (data: any) => ({ data });

/**
 * Maps raw Firebase Auth error codes to human-friendly messages.
 */
const friendlyAuthError = (err: any): string => {
  const code: string = err?.code || '';
  const map: Record<string, string> = {
    'auth/invalid-credential':   'Incorrect email or password. Please try again.',
    'auth/user-not-found':       'No account found with that email address.',
    'auth/wrong-password':       'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password':        'Password must be at least 6 characters long.',
    'auth/invalid-email':        'Please enter a valid email address.',
    'auth/user-disabled':        'This account has been disabled. Please contact support.',
    'auth/too-many-requests':    'Too many failed attempts. Please wait a few minutes and try again.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/requires-recent-login': 'Please sign out and sign back in before making this change.',
  };
  return map[code] || err?.message || 'Something went wrong. Please try again.';
};

// Helper to get current user's organizationId
const getCurrentUserOrgId = async () => {
  if (!auth.currentUser) return null;
  const docSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
  return docSnap.data()?.organizationId || null;
};

// KYC Verification is now handled manually by admins in the dashboard.

export const organizationService = {
  registerOrganization: async (companyName: string, adminUserId: string, documents: { cacUrl?: string, memartUrl?: string, applicationUrl?: string }) => {
    // Free tier activation immediately: Organizations get 10 free QR codes without paying
    const docRef = await addDoc(collection(db, 'organizations'), {
      companyName,
      adminUserId,
      status: 'active',
      documents: documents || {},
      subscriptionTier: 'free',
      subscriptionStatus: 'active',
      createdAt: serverTimestamp()
    });
    // Immediately update the organization doc with its own generated ID for consistency
    await updateDoc(doc(db, 'organizations', docRef.id), { id: docRef.id });
    
    // Set the user's organizationId and give them master_admin role on free plan
    await updateDoc(doc(db, 'users', adminUserId), { 
      organizationId: docRef.id,
      role: 'master_admin',
      subscriptionTier: 'free',
      subscriptionStatus: 'active'
    });
    
    return toAxios({ id: docRef.id, message: 'Organization account created successfully on the Free Plan (10 Free QR Codes)!' });
  },
  getOrganizations: async (status?: string) => {
    let q = query(collection(db, 'organizations'));
    if (status) q = query(q, where('status', '==', status));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  },
  approveOrganization: async (orgId: string, adminUserId: string) => {
    // 1. Mark Organization Active
    await updateDoc(doc(db, 'organizations', orgId), { status: 'active' });
    
    // 2. Mark Admin User Active and set role from pending to master_admin
    await updateDoc(doc(db, 'users', adminUserId), {
      role: 'master_admin',
      requestedRole: null,
      isActive: true
    });
    return toAxios({ message: 'Organization approved successfully' });
  },
  updateSubscription: async (orgId: string, updates: Partial<Organization>) => {
    await updateDoc(doc(db, 'organizations', orgId), updates);
    return toAxios({ message: 'Subscription updated successfully' });
  },
  getOrganization: async (orgId: string) => {
    const docSnap = await getDoc(doc(db, 'organizations', orgId));
    return toAxios({ id: docSnap.id, ...docSnap.data() });
  }
};

export const settingsService = {
  getBankDetails: async () => {
    const docSnap = await getDoc(doc(db, 'system_settings', 'billing'));
    return toAxios(docSnap.data()?.bankDetails || null);
  },
  updateBankDetails: async (bankDetails: any) => {
    await setDoc(doc(db, 'system_settings', 'billing'), {
      bankDetails,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return toAxios({ message: 'Bank details updated successfully' });
  }
};

export const notificationService = {
  getNotificationsForOrg: async (orgId: string) => {
    const q = query(collection(db, 'notifications'), where('organizationId', '==', orgId));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  },
  sendNotification: async (orgId: string, title: string, message: string, type: string = 'info') => {
    const docRef = await addDoc(collection(db, 'notifications'), {
      organizationId: orgId,
      title,
      message,
      type,
      isRead: false,
      createdAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, message: 'Notification sent' });
  },
  markAsRead: async (notificationId: string) => {
    await updateDoc(doc(db, 'notifications', notificationId), { isRead: true });
    return toAxios({ message: 'Notification marked as read' });
  }
};

export const authService = {
  register: async (
    name: string, 
    email: string, 
    password: string, 
    requestedRole: string = 'owner', 
    idType?: string, 
    idNumber?: string, 
    idCardUrl?: string,
    category: 'individual' | 'company' | 'staff' = 'individual'
  ) => {
    try {
      // 1. Create Firebase Auth User
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Determine role: staff requests are pending verification, 
      // while individuals and organizations are active on the Freemium Starter plan immediately.
      const role = category === 'staff' 
        ? 'pending' 
        : (category === 'company' ? 'master_admin' : (requestedRole || 'owner'));

      // 2. Save to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        name, 
        email, 
        role,
        requestedRole: category === 'staff' ? requestedRole : null,
        idType: idType || null,
        idNumber: idNumber || null,
        idCardUrl: idCardUrl || null,
        kycVerified: false,
        subscriptionTier: 'free',
        subscriptionStatus: 'active',
        accountCategory: category,
        createdAt: serverTimestamp()
      });

      return toAxios({ 
        message: category === 'staff' 
          ? 'Staff onboarding submitted for operational review.' 
          : 'Free account activated successfully! Start tagging equipment with your free QR codes.', 
        role, 
        userId: user.uid 
      });
    } catch (err: any) {
      throw new Error(friendlyAuthError(err));
    }
  },
  login: async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return toAxios({ token: userCredential.user.uid });
    } catch (err: any) {
      throw new Error(friendlyAuthError(err));
    }
  },
  getProfile: async () => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const docSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    return toAxios(docSnap.data());
  },
  resetPassword: async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return toAxios({ message: 'Password reset email sent' });
    } catch (err: any) {
      throw new Error(friendlyAuthError(err));
    }
  }
};

export const userService = {
  getAllUsers: async () => {
    if (!auth.currentUser) return toAxios([]);
    
    // Check role and org of the current user
    const docSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    const currentUser = docSnap.data();
    
    let q = query(collection(db, 'users'));
    
    // If not super admin, strictly filter by their organizationId
    if (currentUser?.role !== 'super_admin' && currentUser?.organizationId) {
      q = query(q, where('organizationId', '==', currentUser.organizationId));
    } else if (currentUser?.role !== 'super_admin' && !currentUser?.organizationId) {
      // If they somehow have no org and aren't super admin, return empty to prevent data leak
      return toAxios([]);
    }

    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  },
  createUser: async (userData: any) => {
    // Call the createAdminUser Cloud Function which uses the Firebase Admin SDK.
    // This bypasses reCAPTCHA Enterprise restrictions that block client-side
    // createUserWithEmailAndPassword on secondary app instances.
    try {
      const { name, email, password, role, organizationId, certifications } = userData;

      if (!password) throw new Error('A temporary password is required to create an account.');
      if (!email)    throw new Error('An email address is required.');

      const fn = httpsCallable(functions, 'createAdminUser');
      const result: any = await fn({
        name,
        email,
        password,
        role:           role || 'pending',
        organizationId: organizationId || null,
        certifications: certifications || [],
      });

      return toAxios({ _id: result.data.uid, ...userData });
    } catch (err: any) {
      // Cloud Function errors come back as err.message already friendly
      throw new Error(err?.message || 'Failed to create user.');
    }
  },
  updateUserRole: async (userId: string, roleData: any) => {
    try {
      const cleanData: Record<string, any> = {};
      Object.keys(roleData).forEach(key => {
        if (roleData[key] !== undefined) {
          cleanData[key] = roleData[key];
        }
      });
      await updateDoc(doc(db, 'users', userId), cleanData);
      return toAxios({ message: 'Role updated successfully' });
    } catch (err: any) {
      console.error('Error updating user role:', err);
      throw new Error(err?.message || 'Failed to update user role');
    }
  },
  deleteUser: async (userId: string) => {
    await deleteDoc(doc(db, 'users', userId));
    return toAxios({ message: 'User deleted' });
  },
  searchUsers: async (searchQuery?: string, role?: string, state?: string) => {
    // Basic implementation (Firestore doesn't support full-text search out of the box)
    let q = query(collection(db, 'users'));
    if (role) q = query(q, where('role', '==', role));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  }
};

export const siteService = {
  uploadSites: async (sites: any[]) => {
    const batch = writeBatch(db);
    const efmsSitesRef = collection(db, 'efms_sites');
    sites.forEach(site => {
      const docRef = doc(efmsSitesRef);
      batch.set(docRef, site);
    });
    await batch.commit();
    return toAxios({ message: `${sites.length} sites uploaded successfully` });
  },
  searchSites: async (siteId: string, state?: string) => {
    const q = query(collection(db, 'efms_sites'), where('siteId', '==', siteId));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  },
  getSiteById: async (siteId: string) => {
    const docSnap = await getDoc(doc(db, 'efms_sites', siteId));
    return toAxios({ _id: docSnap.id, ...docSnap.data() });
  },
  getNearestSites: async (latitude: number, longitude: number, radius: number) => {
    // simplified
    const snap = await getDocs(query(collection(db, 'efms_sites')));
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  }
};

export const reportService = {
  createReport: async (reportData: any) => {
    const docRef = await addDoc(collection(db, 'reports'), { ...reportData, createdAt: serverTimestamp() });
    return toAxios({ _id: docRef.id, ...reportData });
  },
  submitReport: async (reportId: string) => {
    await updateDoc(doc(db, 'reports', reportId), { status: 'pending' });
    return toAxios({ message: 'Report submitted' });
  },
  approveReport: async (reportId: string, status: string, signature?: string) => {
    await updateDoc(doc(db, 'reports', reportId), { status, signature });
    return toAxios({ message: 'Report approved' });
  },
  getReports: async (status?: string, reportType?: string) => {
    let q = query(collection(db, 'reports'));
    if (status) q = query(q, where('status', '==', status));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  },
  getReportById: async (reportId: string) => {
    const docSnap = await getDoc(doc(db, 'reports', reportId));
    return toAxios({ _id: docSnap.id, ...docSnap.data() });
  }
};

export const signatureService = {
  uploadSignature: async (signatureData: string) => {
    if (!auth.currentUser) return;
    await updateDoc(doc(db, 'users', auth.currentUser.uid), { signature: signatureData });
    return toAxios({ message: 'Signature saved' });
  },
  getMySignature: async () => {
    if (!auth.currentUser) return toAxios(null);
    const docSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    return toAxios({ signature: docSnap.data()?.signature || null });
  },
  deleteSignature: async () => {
    if (!auth.currentUser) return;
    await updateDoc(doc(db, 'users', auth.currentUser.uid), { signature: null });
    return toAxios({ message: 'Signature deleted' });
  }
};

export const assetService = {
  getInventory: async (assetType?: string) => {
    let q = query(collection(db, 'assets'));
    if (assetType) q = query(q, where('assetType', '==', assetType));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  },
  checkoutAsset: async (assetId: string, staffId: string) => {
    await updateDoc(doc(db, 'assets', assetId), { status: 'in_use', currentHolder: staffId });
    return toAxios({ message: 'Asset checked out' });
  },
  returnAsset: async (assetId: string) => {
    await updateDoc(doc(db, 'assets', assetId), { status: 'available', currentHolder: null });
    return toAxios({ message: 'Asset returned' });
  },
  updateAssetStatus: async (assetId: string, status: string, notes: string) => {
    await updateDoc(doc(db, 'assets', assetId), { status, statusNotes: notes });
    return toAxios({ message: 'Asset status updated' });
  }
};

export const ticketService = {
  createTicket: async (ticketData: any) => {
    const docRef = await addDoc(collection(db, 'tickets'), { ...ticketData, createdAt: serverTimestamp() });
    return toAxios({ _id: docRef.id, ...ticketData });
  },
  assignTicket: async (ticketId: string, assignedTeam: string) => {
    await updateDoc(doc(db, 'tickets', ticketId), { assignedTeam });
    return toAxios({ message: 'Ticket assigned' });
  },
  updateTicketStatus: async (ticketId: string, status: string) => {
    await updateDoc(doc(db, 'tickets', ticketId), { status });
    return toAxios({ message: 'Ticket updated' });
  },
  getTickets: async (status?: string, severity?: string) => {
    let q = query(collection(db, 'tickets'));
    if (status) q = query(q, where('status', '==', status));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
  }
};

// --- New Verisure Services ---

export const verisureService = {
  // --- New Universal VERISURE Item Methods ---
  registerItem: async (itemData: any) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const orgId = await getCurrentUserOrgId();
    const docRef = await addDoc(collection(db, 'verisure_items'), {
      ...itemData,
      createdBy: auth.currentUser.uid,
      ownerUserId: auth.currentUser.uid,
      organizationId: orgId,
      registeredAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...itemData });
  },
  getItemById: async (itemId: string) => {
    try {
      const docSnap = await getDoc(doc(db, 'verisure_items', itemId));
      if (docSnap.exists()) {
        return toAxios({ id: docSnap.id, ...docSnap.data() });
      }
    } catch {}

    try {
      const q = query(collection(db, 'verisure_items'), where('assetId', '==', itemId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        return toAxios({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
    } catch {}

    // Check local storage cache
    try {
      const local = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
      const found = local.find((it: any) => it.id === itemId || it.assetId === itemId);
      if (found) return toAxios(found);
    } catch {}

    return toAxios(null);
  },
  searchItems: async (searchQuery?: string) => {
    const snap = await getDocs(query(collection(db, 'verisure_items')));
    const dbItems: any[] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const local = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
    const map = new Map<string, any>();
    dbItems.forEach((it: any) => map.set(it.assetId || it.id, it));
    local.forEach((it: any) => {
      const k = it.assetId || it.id;
      if (!map.has(k)) map.set(k, it);
    });
    return toAxios(Array.from(map.values()));
  },
  getAllItems: async () => {
    const orgId = await getCurrentUserOrgId();
    let dbItems: any[] = [];
    try {
      let q = query(collection(db, 'verisure_items'));
      if (orgId) {
        q = query(q, where('organizationId', '==', orgId));
      }
      const snap = await getDocs(q);
      dbItems = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Could not fetch items from firestore:', e);
    }

    try {
      const local = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
      const map = new Map<string, any>();
      dbItems.forEach((it: any) => map.set(it.assetId || it.id, it));
      local.forEach((it: any) => {
        const k = it.assetId || it.id;
        if (!map.has(k)) map.set(k, it);
      });
      return toAxios(Array.from(map.values()));
    } catch {
      return toAxios(dbItems);
    }
  },

  // --- New Universal VERISURE Repair Methods ---
  logRepair: async (repairData: any) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const orgId = await getCurrentUserOrgId();
    const docRef = await addDoc(collection(db, 'verisure_repairs'), {
      ...repairData,
      technicianId: auth.currentUser.uid,
      organizationId: orgId,
      immutable: true,
      createdAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...repairData });
  },
  getRepairsByItem: async (itemId: string) => {
    const q = query(collection(db, 'verisure_repairs'), where('itemId', '==', itemId));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a:any, b:any) => b.date - a.date));
  },
  getAllRepairs: async () => {
    const orgId = await getCurrentUserOrgId();
    let q = query(collection(db, 'verisure_repairs'));
    if (orgId) {
      q = query(q, where('organizationId', '==', orgId));
    }
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a:any, b:any) => b.createdAt - a.createdAt));
  },

  // --- New Universal VERISURE Claims Methods ---
  fileClaim: async (claimData: any) => {
    const orgId = await getCurrentUserOrgId();
    const docRef = await addDoc(collection(db, 'verisure_claims'), {
      ...claimData,
      organizationId: orgId,
      status: 'pending',
      submittedAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...claimData });
  },
  getClaims: async () => {
    const orgId = await getCurrentUserOrgId();
    let q = query(collection(db, 'verisure_claims'));
    if (orgId) {
      q = query(q, where('organizationId', '==', orgId));
    }
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  },
  updateClaimStatus: async (claimId: string, status: string) => {
    await updateDoc(doc(db, 'verisure_claims', claimId), { status, resolvedAt: status === 'approved' || status === 'rejected' ? serverTimestamp() : null });
    return toAxios({ message: 'Claim status updated' });
  },

  // --- New Universal VERISURE Technician Methods ---
  getTechnicianProfile: async (userId: string) => {
    const q = query(collection(db, 'verisure_technicians'), where('userId', '==', userId));
    const snap = await getDocs(q);
    if (snap.empty) return toAxios(null);
    return toAxios({ id: snap.docs[0].id, ...snap.docs[0].data() });
  },
  upsertTechnicianProfile: async (userId: string, data: any) => {
    const q = query(collection(db, 'verisure_technicians'), where('userId', '==', userId));
    const snap = await getDocs(q);
    if (snap.empty) {
      const docRef = await addDoc(collection(db, 'verisure_technicians'), { ...data, userId, createdAt: serverTimestamp() });
      return toAxios({ id: docRef.id, ...data });
    } else {
      const docId = snap.docs[0].id;
      await updateDoc(doc(db, 'verisure_technicians', docId), data);
      return toAxios({ id: docId, ...data });
    }
  },

  // --- Blank Outlet QR Code Management (Master & Super Admin) ---
  createBlankQr: async (blankQrData: any) => {
    const tagId = blankQrData.tagId || `VRS-TAG-${Math.floor(10000 + Math.random() * 90000)}`;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://verisure.online';
    const payload = {
      tagId,
      batchId: blankQrData.batchId || `BATCH-${new Date().getFullYear()}-01`,
      outletName: blankQrData.outletName || 'Main Outlet',
      outletLocation: blankQrData.outletLocation || 'Storefront Distribution',
      status: 'unclaimed',
      qrPayloadUrl: `${origin}/tag/${tagId}`,
      notes: blankQrData.notes || '',
      createdBy: auth.currentUser?.uid || 'admin',
      createdByName: auth.currentUser?.displayName || 'Admin',
      createdAt: serverTimestamp()
    };

    let docId = tagId;
    try {
      const docRef = await addDoc(collection(db, 'verisure_blank_qrs'), payload);
      docId = docRef.id;
    } catch (e) {
      console.warn('Firestore blank QR save error, relying on local cache:', e);
    }

    const result = { id: docId, ...payload, createdAt: new Date() };
    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      localStorage.setItem('verisure_blank_qrs_cache', JSON.stringify([result, ...local]));
    } catch {}

    return toAxios(result);
  },

  createBlankQrBatch: async (batchData: {
    outletName: string;
    outletLocation?: string;
    batchId?: string;
    count: number;
    prefix?: string;
    notes?: string;
  }) => {
    const count = Math.min(Math.max(1, batchData.count || 1), 100);
    const prefix = batchData.prefix || 'VRS-TAG-';
    const batchId = batchData.batchId || `BATCH-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://verisure.online';

    const generatedItems: any[] = [];
    for (let i = 0; i < count; i++) {
      const randomSuffix = Math.floor(10000 + Math.random() * 90000);
      const tagId = `${prefix}${randomSuffix}`;
      const payload = {
        tagId,
        batchId,
        outletName: batchData.outletName || 'Retail Outlet',
        outletLocation: batchData.outletLocation || '',
        status: 'unclaimed',
        qrPayloadUrl: `${origin}/tag/${tagId}`,
        notes: batchData.notes || '',
        createdBy: auth.currentUser?.uid || 'admin',
        createdByName: auth.currentUser?.displayName || 'Verisure Admin',
        createdAt: serverTimestamp()
      };

      try {
        const docRef = await addDoc(collection(db, 'verisure_blank_qrs'), payload);
        generatedItems.push({ id: docRef.id, ...payload, createdAt: new Date() });
      } catch (err) {
        generatedItems.push({ id: tagId, ...payload, createdAt: new Date() });
      }
    }

    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      localStorage.setItem('verisure_blank_qrs_cache', JSON.stringify([...generatedItems, ...local]));
    } catch {}

    return toAxios(generatedItems);
  },

  getBlankQrs: async (filter?: { outletName?: string; status?: string }) => {
    let items: any[] = [];
    try {
      let q = query(collection(db, 'verisure_blank_qrs'));
      if (filter?.status) {
        q = query(q, where('status', '==', filter.status));
      }
      if (filter?.outletName) {
        q = query(q, where('outletName', '==', filter.outletName));
      }
      const snap = await getDocs(q);
      items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('Could not fetch blank QRs from Firestore, reading cache:', e);
    }

    // Merge with local cache
    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      const map = new Map<string, any>();
      items.forEach(it => map.set(it.tagId || it.id, it));
      local.forEach((it: any) => {
        const key = it.tagId || it.id;
        if (!map.has(key)) map.set(key, it);
      });
      items = Array.from(map.values());
    } catch {}

    return toAxios(items);
  },

  getBlankQrByTagId: async (tagId: string) => {
    // 1. Try Firestore query by tagId
    try {
      const q = query(collection(db, 'verisure_blank_qrs'), where('tagId', '==', tagId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        return toAxios({ id: snap.docs[0].id, ...snap.docs[0].data() });
      }
      // Try direct ID lookup
      const docSnap = await getDoc(doc(db, 'verisure_blank_qrs', tagId));
      if (docSnap.exists()) {
        return toAxios({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (e) {
      console.warn('Error fetching blank QR from firestore:', e);
    }

    // 2. Check local storage cache
    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      const found = local.find((t: any) => t.tagId === tagId || t.id === tagId);
      if (found) return toAxios(found);
    } catch {}

    return toAxios(null);
  },

  deleteBlankQr: async (tagIdOrId: string) => {
    try {
      // 1. Delete from Firestore if exists
      const docRef = doc(db, 'verisure_blank_qrs', tagIdOrId);
      await deleteDoc(docRef).catch(() => {});

      const q = query(collection(db, 'verisure_blank_qrs'), where('tagId', '==', tagIdOrId));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        await deleteDoc(doc(db, 'verisure_blank_qrs', d.id)).catch(() => {});
      }
    } catch (e) {
      console.warn('Error deleting blank QR from firestore:', e);
    }

    // 2. Remove from local storage cache
    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      const filtered = local.filter((t: any) => t.tagId !== tagIdOrId && t.id !== tagIdOrId);
      localStorage.setItem('verisure_blank_qrs_cache', JSON.stringify(filtered));
    } catch {}

    return toAxios({ success: true, deleted: tagIdOrId });
  },

  deleteBlankQrsBulk: async (idsOrTagIds: string[]) => {
    const toDeleteSet = new Set(idsOrTagIds);
    let count = 0;

    try {
      // Try batch delete in firestore
      const batch = writeBatch(db);
      let batchCount = 0;

      for (const idOrTag of idsOrTagIds) {
        batch.delete(doc(db, 'verisure_blank_qrs', idOrTag));
        batchCount++;
      }

      const q = query(collection(db, 'verisure_blank_qrs'));
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const data = d.data();
        if (toDeleteSet.has(d.id) || toDeleteSet.has(data.tagId)) {
          batch.delete(doc(db, 'verisure_blank_qrs', d.id));
          batchCount++;
        }
      }

      if (batchCount > 0) {
        await batch.commit().catch(() => {});
      }
    } catch (e) {
      console.warn('Could not batch delete from firestore:', e);
    }

    // Purge from local storage cache
    try {
      const local = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      const filtered = local.filter((t: any) => !toDeleteSet.has(t.tagId) && !toDeleteSet.has(t.id));
      count = local.length - filtered.length;
      localStorage.setItem('verisure_blank_qrs_cache', JSON.stringify(filtered));
    } catch {}

    return toAxios({ success: true, count: Math.max(count, idsOrTagIds.length) });
  },

  claimAndRegisterDevice: async (tagId: string, deviceData: any) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://verisure.online';
    const orgId = await getCurrentUserOrgId();

    // 1. Prepare Item Payload
    const itemPayload = {
      assetId: tagId,
      make: deviceData.make,
      model: deviceData.model,
      category: deviceData.category || 'consumer_electronics',
      serialNumber: deviceData.serialNumber || '',
      year: Number(deviceData.year) || new Date().getFullYear(),
      ownerName: deviceData.ownerName,
      ownerEmail: deviceData.ownerEmail || '',
      ownerPhone: deviceData.ownerPhone || '',
      outletOrigin: deviceData.outletOrigin || '',
      conditionScore: Number(deviceData.conditionScore) || 100,
      estimatedOriginalPrice: Number(deviceData.estimatedOriginalPrice) || 0,
      priceCurrency: deviceData.priceCurrency || 'NGN',
      qrCodeUrl: `${origin}/item/${tagId}`,
      registeredAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || 'outlet_customer',
      ownerUserId: auth.currentUser?.uid || null,
      organizationId: orgId || null
    };

    // 2. Save Item in Firestore
    let itemDocId = tagId;
    try {
      const docRef = await addDoc(collection(db, 'verisure_items'), itemPayload);
      itemDocId = docRef.id;
    } catch (e) {
      console.warn('Could not register item to firestore directly:', e);
    }

    // 3. Mark Blank QR as Registered in Firestore
    try {
      const q = query(collection(db, 'verisure_blank_qrs'), where('tagId', '==', tagId));
      const snap = await getDocs(q);
      const updates = {
        status: 'registered',
        claimedAt: serverTimestamp(),
        claimedByName: deviceData.ownerName,
        claimedByEmail: deviceData.ownerEmail || '',
        claimedByPhone: deviceData.ownerPhone || '',
        itemId: itemDocId,
        deviceSummary: {
          make: deviceData.make,
          model: deviceData.model,
          category: deviceData.category,
          serialNumber: deviceData.serialNumber || ''
        }
      };

      if (!snap.empty) {
        await updateDoc(doc(db, 'verisure_blank_qrs', snap.docs[0].id), updates);
      }
    } catch (e) {
      console.warn('Could not update blank QR record in firestore:', e);
    }

    // 4. Update Local Storage Cache
    const registeredItem = { id: itemDocId, ...itemPayload, registeredAt: new Date() };
    try {
      // Update blank QRs cache
      const localQrs = JSON.parse(localStorage.getItem('verisure_blank_qrs_cache') || '[]');
      const updatedQrs = localQrs.map((q: any) => {
        if (q.tagId === tagId || q.id === tagId) {
          return {
            ...q,
            status: 'registered',
            claimedAt: new Date(),
            claimedByName: deviceData.ownerName,
            itemId: itemDocId,
            deviceSummary: {
              make: deviceData.make,
              model: deviceData.model,
              category: deviceData.category,
              serialNumber: deviceData.serialNumber || ''
            }
          };
        }
        return q;
      });
      localStorage.setItem('verisure_blank_qrs_cache', JSON.stringify(updatedQrs));

      // Cache registered item
      const localItems = JSON.parse(localStorage.getItem('verisure_items_cache') || '[]');
      localStorage.setItem('verisure_items_cache', JSON.stringify([registeredItem, ...localItems]));
    } catch {}

    return toAxios({ item: registeredItem });
  },

  createSite: async (siteData: any) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const docRef = await addDoc(collection(db, 'verisure_sites'), {
      ...siteData,
      createdBy: auth.currentUser.uid,
      createdAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...siteData });
  },
  getSites: async () => {
    const snap = await getDocs(query(collection(db, 'verisure_sites')));
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  },
  createCabinet: async (cabinetData: any) => {
    const docRef = await addDoc(collection(db, 'verisure_cabinets'), {
      ...cabinetData,
      createdAt: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...cabinetData });
  },
  getCabinetsBySite: async (siteId: string) => {
    const q = query(collection(db, 'verisure_cabinets'), where('siteId', '==', siteId));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  },
  getCabinetById: async (cabinetId: string) => {
    const docSnap = await getDoc(doc(db, 'verisure_cabinets', cabinetId));
    return toAxios({ id: docSnap.id, ...docSnap.data() });
  },
  logMaintenance: async (logData: any) => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const docRef = await addDoc(collection(db, 'verisure_logs'), {
      ...logData,
      technicianId: auth.currentUser.uid,
      timestamp: serverTimestamp()
    });
    return toAxios({ id: docRef.id, ...logData });
  },
  getCabinetLogs: async (cabinetId: string) => {
    const q = query(collection(db, 'verisure_logs'), where('cabinetId', '==', cabinetId));
    const snap = await getDocs(q);
    return toAxios(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a:any, b:any) => b.timestamp - a.timestamp));
  }
};

export const surveyService = {
  getResponses: async () => {
    try {
      const q = query(collection(db, 'verisure_survey_responses'));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      return toAxios(list);
    } catch (e) {
      console.warn('Could not fetch survey responses:', e);
      return toAxios([]);
    }
  }
};
